import React, { Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import loadLazyPage from "@utility/loadLazyPage";

// 🚀 CRITICAL IMPORTS ONLY (necessari per router base)
import Root from "./root";
import LoadingScreen from "./loadingScreen/loadingScreen";
import ErrorPage from "@pages/error/index";
import ProtectedRoute from "@components/protectedRoute/protectedRoute";
import RoleRoute from "@components/protectedRoute/RoleRoute";
import AdminGate from "@pages/adminSocial/AdminGate";
import { isShellV2Enabled } from "@containers/appShell/shellFlag";
import CookieSolution from "@pages/landing/CookieSolution/CookieSolution";

// 🎯 ALL PAGES LAZY-LOADED - Riduce bundle iniziale drasticamente

import {
  loginAction,
  signUpAction,
  resetPasswordAction,
  checkVerifyEmailAction,
  uploadNotesAction,
  userProfileAction,
  getFeedbackQuestionAction,
} from "./actions/actions";

// 🚀 LCP: kill the home→landing chunk WATERFALL without bloating the entrypoint.
// The homepage "/" landing route stays code-split (so framer-motion, lucide and the
// below-the-fold sections stay OUT of the initial entry), but we kick off the landing
// chunk download eagerly here — at main-bundle evaluation time — instead of waiting
// for React Router to resolve `lazy` only after first render. router.js is part of
// the main entry, so this `import()` starts the fetch as soon as the main bundle is
// parsed, overlapping it with Firebase init etc. By the time the router renders the
// index route, the chunk (and its module promise, reused below) is already in flight
// or cached, removing the serial round-trip. Eager-importing the component instead
// measured +144KB gzip on the entry (framer-motion/lucide/landing graph pulled
// forward) — net-negative for LCP — so we warm the chunk rather than inline it.
// NB: defined AFTER all imports to satisfy eslint import/first (the bare
// landingChunk() call is module-body code and must not sit between imports).
// Dal 08/09 la home e' la landing nuova (CEO: "siamo arrivati, la rendiamo
// reale, sostitutiva di quella attuale"). La vecchia `pages/landing/landing`
// non e' piu' montata; restano vivi i suoi Header/Footer sulle pagine vecchie
// (faculty, schede prodotto, prezzi) che non sono state rifatte.
const landingChunk = () => import(/* webpackPrefetch: true */ "@pages/landingNuova/index");
landingChunk(); // warm the cache immediately (parallel with main bundle execution)

/* /landing-nuova era la rotta di lavoro: chi ha il link vecchio (chat,
   diario, screenshot) arriva in home, con la stessa ancora se c'era. */
const VersoHome = () => {
  const { hash } = useLocation();
  return <Navigate to={`/${hash || ''}`} replace />;
};

// 🔐 Dashboard per ruolo: il guard decide PRIMA che il chunk parta.
// Con `lazy={() => import(...)}` a livello di rotta l'import parte al match,
// quindi anche chi non ha il ruolo scaricava il bundle e vedeva un lampo di
// dashboard non sua prima del navigate('/home') interno alla pagina.
// React.lazy invece importa al RENDER: se RoleRoute risponde <Navigate>, il
// componente non viene mai renderizzato e il chunk non viene mai chiesto.
const ParentDashboardPage = React.lazy(() => import("@pages/parentDashboard/index"));
const TeacherDashboardPage = React.lazy(() => import("@pages/teacherDashboard/index"));

const roleDashboardRoute = (role, LazyPage) => (
  <RoleRoute
    role={role}
    fallback={<LoadingScreen />}
    element={
      <Suspense fallback={<LoadingScreen />}>
        <LazyPage />
      </Suspense>
    }
  />
);

// Il banner dei cookie (Iubenda) sopra TUTTE le rotte (STU-480). Prima viveva
// in Root, quindi le pagine pubbliche montate fuori da Root (/maturita, le
// landing di funzione, /sicurezza, /piani-e-prezzi, /blog, /welcome,
// /payment-*, /privacy) non lo mostravano mai: chi ci arrivava direttamente non
// poteva dare il consenso, e con Google Analytics negato di default restava
// senza cookie per tutta la visita.
const ConsensoCookie = () => (
  <>
    <CookieSolution />
    <Outlet />
  </>
);

// 🚀 Router con Code Splitting Completo
// 🆕 Future flags per preparazione a React Router v7
export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<ConsensoCookie />}>
      {/* 🎓 Maturita Landing - Public standalone page (no header/layout wrapper) */}
      <Route path="/maturita" lazy={() => import("@pages/maturitaLanding/index").then(m => ({ Component: m.default }))} />

      {/* 📚 Feature Landing Pages - Public standalone pages */}
      <Route path="/esami-orali" lazy={() => import("@pages/esamiOraliLanding/index").then(m => ({ Component: m.default }))} />
      <Route path="/materiale-didattico" lazy={() => import("@pages/materialeDidatticoLanding/index").then(m => ({ Component: m.default }))} />
      <Route path="/ripasso-ai" lazy={() => import("@pages/ripassoAILanding/index").then(m => ({ Component: m.default }))} />
      <Route path="/piano-studio" lazy={() => import("@pages/pianoStudioLanding/index").then(m => ({ Component: m.default }))} />
      <Route path="/progressi-landing" lazy={() => import("@pages/progressiLanding/index").then(m => ({ Component: m.default }))} />

      {/* 🤝 UniversityBox OAuth callback */}
      <Route path="/universitybox/callback" lazy={() => import("@pages/universityboxCallback/index").then(m => ({ Component: m.default }))} />

      {/* 🌐 Independent routes - Lazy loaded */}
      {/* 08/09: Chi siamo nella cornice della landing nuova (la pagina vecchia non era piu' vera) */}
      <Route path="/chi-siamo" lazy={() => import("@pages/landingNuova/ChiSiamo").then(m => ({ Component: m.default }))} />
      {/* 08/09: la tecnologia e' una pagina per i curiosi, linkata solo dal piede */}
      <Route path="/tecnologia" lazy={() => import("@pages/landingNuova/Tecnologia").then(m => ({ Component: m.default }))} />
      {/* Sicurezza e minori (STU-479): pagina pubblica, linkata dall'header e dalla home */}
      <Route path="/sicurezza" lazy={() => import("@pages/landingNuova/Sicurezza").then(m => ({ Component: m.default }))} />
      <Route path="/lavora-con-noi" lazy={() => import("@pages/joinUs").then(m => ({ Component: m.default }))} />
      <Route path="/faculty/:tab?" lazy={() => import("@pages/landing/Faculty/Faculty").then(m => ({ Component: m.default }))} />
      <Route path="/piani-e-prezzi" lazy={() => import("@pages/pricing/index").then(m => ({ Component: m.default }))} />
      <Route path="/termini-e-condizioni" lazy={() => import("@pages/terms/index").then(m => ({ Component: m.default }))} />
      {/* STU-124: alias linkato dall'app mobile → canonico /termini-e-condizioni */}
      <Route path="/termini" element={<Navigate to="/termini-e-condizioni" replace />} />
      <Route path="/privacy" lazy={() => import("@pages/privacy/index").then(m => ({ Component: m.default }))} />
      {/* STU-124: pagina assistenza pubblica aperta dall'app mobile (Impostazioni → Aiuto) */}
      <Route path="/help" lazy={() => import("@pages/help/index").then(m => ({ Component: m.default }))} />
      <Route path="/welcome" lazy={() => import("@pages/welcome/index").then(m => ({ Component: m.default }))} />
      <Route path="/age-restricted" lazy={() => import("@pages/ageRestricted/index").then(m => ({ Component: m.default }))} />
      <Route path="/payment-success" lazy={() => import("@pages/paymentSuccess/index").then(m => ({ Component: m.default }))} />
      <Route path="/payment-completed" lazy={() => import("@pages/paymentSuccess/index").then(m => ({ Component: m.default }))} />

      {/* 📝 Blog pages - Lazy loaded */}
      <Route path="/blog" lazy={() => import("@pages/blog/index").then(m => ({ Component: m.default }))} />
      <Route path="/blog/:slug" lazy={() => import("@pages/blog/BlogArticle").then(m => ({ Component: m.default }))} />
      {/* Archivio pubblico delle novita di prodotto. Stessa fonte del
          pannello in app (containers/layout monta ProductUpdatePanel):
          solo entry approvate a mano da /admin/marketing/novita. */}
      <Route path="/novita" lazy={() => import("@pages/novita/index").then(m => ({ Component: m.default }))} />

      {/* 🔧 Admin pages - Unlisted + AdminGate (Firebase auth + allowlist email admin) */}
      <Route element={<AdminGate />}>
        {/* New marketing hub: every /admin/marketing/* page mounts under
            the shared MarketingLayout (sidebar + outlet). */}
        <Route
          element={null}
          lazy={() => import("@pages/adminMarketing/MarketingLayout").then(m => ({ Component: m.default }))}
        >
          <Route
            path="/admin/marketing"
            lazy={() => import("@pages/adminMarketing/index").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/churn-vivo"
            lazy={() => import("@pages/adminChurnVivo/index").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/agents"
            lazy={() => import("@pages/adminMarketing/AgentsHub").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/agents/:id"
            lazy={() => import("@pages/adminMarketing/AgentDetail").then(m => ({ Component: m.default }))}
          />
          {/* Plugin Manager alias — reuses the existing SkillsPage from
              adminSocial. Promoted to its own admin/marketing route once
              the editor diverges. */}
          <Route
            path="/admin/marketing/plugins"
            lazy={() => import("@pages/adminSocial/SkillsPage").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/blog"
            lazy={() => import("@pages/adminMarketing/BlogDashboard").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/social"
            lazy={() => import("@pages/adminMarketing/SocialDashboard").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/seo"
            lazy={() => import("@pages/adminMarketing/SeoDashboard").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/ops"
            lazy={() => import("@pages/adminMarketing/OpsDashboard").then(m => ({ Component: m.default }))}
          />
          {/* Churn = la finestra viva (dal 01/09). La vecchia Mission Control
              resta raggiungibile su /churn-storico finche' serve: le coorti su
              120 giorni parlano di un'applicazione precedente. */}
          <Route
            path="/admin/marketing/churn"
            lazy={() => import("@pages/adminChurnVivo/index").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/churn-storico"
            lazy={() => import("@pages/adminMarketing/ChurnDashboard").then(m => ({ Component: m.default }))}
          />
          <Route
            path="/admin/marketing/novita"
            lazy={() => import("@pages/adminMarketing/NovitaDashboard").then(m => ({ Component: m.default }))}
          />
        </Route>

        {/* Legacy admin/social tree — kept unchanged so existing
            bookmarks / Slack links still resolve. */}
        <Route
          path="/admin/social"
          lazy={() => import("@pages/adminSocial/index").then(m => ({ Component: m.default }))}
        />
        <Route
          path="/admin/social/skills"
          lazy={() => import("@pages/adminSocial/SkillsPage").then(m => ({ Component: m.default }))}
        />
        <Route
          path="/admin/social/:slug"
          lazy={() => import("@pages/adminSocial/ArticleDetail").then(m => ({ Component: m.default }))}
        />
      </Route>

      {/* 🏠 Main routes within Root layout */}
      <Route path="/*" element={<Root />} errorElement={<ErrorPage />}>
        {/* 🎯 Home = landing nuova - lazy, but its chunk is warmed eagerly above
            (landingChunk) so the fetch overlaps the main bundle instead of
            starting after render. */}
        <Route index={true} lazy={() => landingChunk().then(m => ({ Component: m.default }))} />

        {/* la vecchia rotta di lavoro della landing nuova: promossa a home
            l'08/09, il link resta buono */}
        <Route path="landing-nuova" element={<VersoHome />} />

        {/* 🔐 Auth pages - Lazy loaded */}
        <Route path="signIn" action={loginAction} lazy={() => import("@pages/signIn/index").then(m => ({ Component: m.default }))} />
        <Route path="signUp" action={signUpAction} lazy={() => import("@pages/signUp/index").then(m => ({ Component: m.default }))} />
        <Route path="verifica-email" lazy={() => import("@pages/verifyEmail/index").then(m => ({ Component: m.default }))} />
        <Route path="controlla-verifica-email" action={checkVerifyEmailAction} lazy={() => import("@pages/checkVerifyEmail/checkVerifyEmail").then(m => ({ Component: m.default }))} />
        <Route path="reimposta-password" action={resetPasswordAction} lazy={() => loadLazyPage("resetPassword")} />

        {/* 🏡 Home nested routes - Lazy loaded con ProtectedRoute wrapper */}
        <Route 
          path="home" 
          lazy={async () => {
            const { default: HomeComponent } = await import("@pages/home/index.js");
            const ProtectedHome = () => <ProtectedRoute element={<HomeComponent />} />;
            return { 
              Component: ProtectedHome
            };
          }}
        >
          {/* Home: con shell v2 attiva l'index è la home nuova (pages/homeV2);
              l'action resta la stessa in entrambi i casi. La v1 non si tocca. */}
          <Route
            index={true}
            action={uploadNotesAction}
            lazy={() =>
              (isShellV2Enabled()
                ? import("@pages/homeV2/index.js")
                : import("@pages/uploadNotes/index.js")
              ).then(m => ({ Component: m.default }))
            }
          />
          {/* Route di sviluppo/demo della home v2: sempre registrata (lazy = zero
              costo bundle), utile per QA con flag spento. */}
          <Route path="v2" lazy={() => import("@pages/homeV2/index.js").then(m => ({ Component: m.default }))} />
          <Route path="materiale" lazy={() => import("@pages/materials/index.js").then(m => ({ Component: m.default }))} />
          <Route path="ripeti/:param?" lazy={() => import("@pages/review/index").then(m => ({ Component: m.default }))} />
          <Route path="profilo" action={userProfileAction} lazy={() => loadLazyPage("userProfile")} />
          {/* Impostazioni: pagina nuova (pages/settings) al posto del profilo
              vecchio, stessa action PATCH. /home/profilo resta finche' il CEO
              non promuove questa (26/08). */}
          <Route path="impostazioni" action={userProfileAction} lazy={() => import("@pages/settings/index.js").then(m => ({ Component: m.default }))} />
          <Route path="feedback" lazy={() => loadLazyPage("feedback")} />
          <Route path="piani" lazy={() => import("@pages/plans/index").then(m => ({ Component: m.default }))} />
          <Route path="referralCode" lazy={() => import("@pages/ReferralCode/ReferralCode.js").then(m => ({ Component: m.default }))} />
          {/* Calendario: la pagina in pages/dashboard è solo il calendario dal refactor
              di maggio 2026 — la "dashboard" vera è Progressi. Il path segue il nome.
              I vecchi URL restano validi: link salvati, email, deep link dall'app. */}
          <Route path="calendario" lazy={() => import("@pages/dashboard/index").then(m => ({ Component: m.default }))} />
          <Route path="dashboard" element={<Navigate to="/home/calendario" replace />} />
          <Route path="dashboard/calendario" element={<Navigate to="/home/calendario" replace />} />
          <Route path="dashboard/performance" element={<Navigate to="/home/progressi" replace />} />
          <Route path="dashboard/progressi" element={<Navigate to="/home/progressi" replace />} />
          <Route path="progressi" lazy={() => import("@pages/progressi/index").then(m => ({ Component: m.default }))} />
          <Route path="dashboard-genitori" element={roleDashboardRoute("parent", ParentDashboardPage)} />
          <Route path="dashboard-professori" element={roleDashboardRoute("teacher", TeacherDashboardPage)} />
          <Route path="punti" lazy={() => import("@pages/myPoints").then(m => ({ Component: m.default }))} />
          <Route path="classifica" lazy={() => import("@pages/ranking/index").then(m => ({ Component: m.default }))} />
          {/* 🎓 Maturita 2026 - Simulazione Orale */}
          <Route path="maturita-esame" lazy={() => import("@pages/maturitaExam/index").then(m => ({ Component: m.default }))} />
          <Route path="maturita-risultati" lazy={() => import("@pages/maturitaExam/MaturitaResults/index").then(m => ({ Component: m.default }))} />
          <Route path="maturita-coach/:materiaId" lazy={() => import("@pages/maturitaCoach/index").then(m => ({ Component: m.default }))} />
          {/* Storico legacy: reindirizza al tab storico dentro ripeti */}
          <Route path="storico" element={<Navigate to="/home/ripeti?tab=history" replace />} />
          {/* 📊 Metrics dashboard - Solo development */}
          <Route path="metrics" lazy={() => import("@pages/metrics/index").then(m => ({ Component: m.default }))} />
        </Route>

        {/* 💬 Chat page - Lazy loaded */}
        <Route
          path="chat/:documentID"
          action={getFeedbackQuestionAction}
          lazy={() => import("@pages/chat/index").then(m => ({ Component: m.default }))}
        />

        {/* 🎤 Voice Conversation Mode - Lazy loaded */}
        <Route
          path="chat/:documentID/voice"
          lazy={() => import("@pages/chat/VoiceConversationMode").then(m => ({ Component: m.default }))}
        />

        {/* 🎓 Tutor AI - Lazy loaded, protected. Behind REACT_APP_TUTOR_AI_ENABLED:
            the wrapper redirects to /home/ripeti when the flag is OFF (defense in depth). */}
        <Route
          path="chat/:documentID/tutor"
          lazy={async () => {
            const { default: TutorAIMode } = await import("@pages/chat/TutorAIMode");
            const ProtectedTutorAI = () => <ProtectedRoute element={<TutorAIMode />} />;
            return { Component: ProtectedTutorAI };
          }}
        />

        {/* 📷 Scatta foto — la schermata che il TELEFONO apre inquadrando il QR
            mostrato sul computer (passaggio di consegne del Centro Fonti).
            Deliberatamente FUORI da ProtectedRoute: la guardia manda chi non
            e' loggato sulla landing senza portarsi dietro l'indirizzo, e uno
            studente che inquadra un QR finirebbe sulla homepage di marketing
            senza capire perche'. La pagina gestisce da sola i tre casi (non
            loggato / altro account / sessione scaduta), e la sicurezza sta
            nelle rules, che pretendono lo stesso uid sui due schermi. */}
        <Route path="scatta/:sessionId" lazy={() => import("@pages/scattaFoto/index").then(m => ({ Component: m.default }))} />

        {/* 📊 Other pages - Lazy loaded */}
        <Route path="risultato-esame" lazy={() => import("@pages/examReport").then(m => ({ Component: m.default }))} />
        
        {/* 🔄 Catch-all route - redirect to /home for any non-existent pages */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>
    </Route>
  ),
  {
    // 🆕 Future flags per preparazione a React Router v7
    future: {
      v7_relativeSplatPath: true,
    },
  }
);
